import type { Map } from 'mapbox-gl';

import { CSSCustomProperty } from '@libs/ui';

import {
  MARKER_WITH_CHECKBOX_SIZES,
  MARKER_WITHOUT_CHECKBOX_SIZES,
  PRICE_MARKER_STYLES,
} from './models';

export function createHighlightPin(): HTMLElement {
  const SIZE = 56;
  const div = document.createElement('div');
  div.innerHTML = `
    <svg width="${SIZE}" height="${SIZE}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="highlight-pulse">
      <mask id="mask0_14282_334" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
      <rect width="24" height="24" fill="#D9D9D9"/>
      </mask>
      <g mask="url(#mask0_14282_334)">
        <path d="M12 1C16.4189 1 19.9999 4.72662 20 9.3252C20 13.9177 12.0214 22.9757 12 23C11.9794 22.9766 4 13.9179 4 9.3252C4.00009 4.72662 7.58112 1 12 1Z" fill="${CSSCustomProperty.colorBrandPop}"/>
        <path d="M11.5495 5.09736C11.7602 5.0508 11.9328 5.07054 12.0856 5.17744C12.2582 5.29809 12.3478 5.47083 12.4176 5.67646C12.5903 6.1812 12.7299 6.70679 12.8327 7.23896C12.9091 7.6381 12.9612 8.04292 12.9928 8.44892C13.031 8.42841 13.0689 8.41573 13.1071 8.39521C13.7463 8.08251 14.3724 7.74247 14.9919 7.3708C15.3503 7.15822 15.7587 7.27747 15.9762 7.66962C16.1804 8.03565 16.0531 8.60716 15.7213 8.80634H15.7223C15.2689 9.07793 14.8154 9.3323 14.3356 9.57783C14.0668 9.72457 13.7914 9.85788 13.5172 9.99091C14.1432 10.4887 14.7759 10.9602 15.4215 11.4196C15.7602 11.6582 15.863 12.1504 15.6774 12.5563C15.4982 12.9485 15.012 13.0822 14.693 12.8561C14.2067 12.516 13.7277 12.1579 13.2545 11.7917C13.1135 11.6861 12.9737 11.5721 12.8327 11.4597C12.7233 12.0507 12.5711 12.6368 12.3669 13.2018C12.3339 13.3416 12.2567 13.4557 12.1803 13.5681C12.1421 13.6201 12.079 13.6876 12.0211 13.7204C11.8486 13.8272 11.6641 13.8402 11.485 13.78C11.2927 13.7142 11.1525 13.5611 11.0563 13.3952C10.9548 13.21 10.9237 12.9362 10.987 12.7497C11.1978 12.1381 11.3633 11.5123 11.4713 10.8679C10.6148 11.1997 9.74649 11.4934 8.8649 11.7458C8.46828 11.8582 8.09759 11.6064 7.97623 11.1677C7.86156 10.748 8.11734 10.2493 8.48111 10.1423C8.84469 10.0367 9.20322 9.93049 9.56021 9.81122C9.8922 9.70566 10.2192 9.59163 10.5446 9.47236C9.83564 8.81411 9.1521 8.12941 8.48795 7.41083C8.34692 7.25858 8.2836 7.0385 8.27701 6.81904C8.27047 6.59416 8.35355 6.3818 8.48795 6.22822C8.6289 6.0624 8.81955 5.98219 8.99869 5.98212C9.19112 5.98212 9.36938 6.08145 9.51041 6.22822C10.1615 6.93319 10.8341 7.61191 11.5299 8.25654C11.4337 7.52563 11.2609 6.80706 11.0251 6.11591C10.9354 5.84982 10.9868 5.58364 11.1081 5.41767C11.2359 5.2517 11.3702 5.13713 11.5495 5.09736ZM14.0856 5.2624C14.5071 5.26265 14.8453 5.61475 14.8454 6.05341C14.8454 6.49218 14.5072 6.84515 14.0856 6.8454C13.6638 6.8454 13.3249 6.49233 13.3249 6.05341C13.325 5.61459 13.6639 5.2624 14.0856 5.2624Z" fill="${CSSCustomProperty.colorWhite}"/>
      </g>
    </svg>`;
  return div;
}

export function loadPriceMarkersPills(map: Map): void {
  const heightPx = 32;
  const borderPx = 1;

  Object.entries(PRICE_MARKER_STYLES).forEach(([state, colors]) => {
    if (
      state === 'selected' ||
      state === 'selected-inspected' ||
      state === 'included' ||
      state === 'included-inspected'
    ) {
      MARKER_WITH_CHECKBOX_SIZES.forEach((bucket) => {
        const svg = createPillSVG(
          bucket.width,
          heightPx,
          borderPx,
          colors.fill,
          colors.stroke
        );
        const img = new Image();
        img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
        img.onload = (): void => {
          const id = `price-pill-${state}-${bucket.name}`;
          if (!map.hasImage(id)) {
            map.addImage(id, img);
          }
        };
      });
    } else {
      MARKER_WITHOUT_CHECKBOX_SIZES.forEach((bucket) => {
        const svg = createPillSVG(
          bucket.width,
          heightPx,
          borderPx,
          colors.fill,
          colors.stroke
        );
        const img = new Image();
        img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
        img.onload = (): void => {
          const id = `price-pill-${state}-${bucket.name}`;
          if (!map.hasImage(id)) {
            map.addImage(id, img);
          }
        };
      });
    }
  });
}

function createPillSVG(
  width: number,
  height: number,
  border: number,
  fill: string,
  stroke: string
): string {
  return `
      <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
        <rect x="0" y="0" width="${width}" height="${height}" rx="${height / 2}" ry="${height / 2}"
              fill="${fill}" stroke="${stroke}" stroke-width="${border}" />
      </svg>
    `;
}
